/**
 * Users API Client
 *
 * Provides interface and real implementation for user management operations
 * via the tenant-management backend.
 *
 * **Org is a mandatory URL segment.** Every member operation is scoped to a
 * specific organization, addressed RESTfully as
 * `/api/tenant-management/orgs/{orgId}/users…`. The caller always supplies the
 * `orgId`:
 * - a **normal admin** passes their own active-org id (sourced from the
 *   backend identity's active org);
 * - a **staff admin** passes the org they picked from the org picker.
 *
 * The backend authorizes the pairing: an `orgId` that differs from the caller's
 * active org requires the `platform.staff.admin` role (else 403); same-org
 * mutations still require `platform.user.admin`.
 *
 * The /api/tenant-management/ service prefix lets the Envoy/Gateway HTTPRoute
 * route all tenant-management traffic to the correct upstream without
 * ambiguity against other /api/* backends.
 *
 * Endpoints:
 * - GET    /api/tenant-management/orgs/{orgId}/users?page=N&pageSize=N      - List org members
 * - POST   /api/tenant-management/orgs/{orgId}/users/invite                 - Invite a member (email + role)
 * - POST   /api/tenant-management/orgs/{orgId}/users/{userId}/resend-invite - Re-send invitation email
 * - DELETE /api/tenant-management/orgs/{orgId}/users/{userId}               - Remove a member from the org
 *
 * @module api/UsersApiClient
 */

import type {
  UserRole,
  ListUsersResponse,
  InviteUserResponse,
  ResendInviteResponse,
  UpdateUserRoleResponse,
  PaginationRequest,
} from '../types';
import { httpGet, httpPost, httpPut, httpDelete, type HttpOptions } from './http';

// =============================================================================
// Interface
// =============================================================================

/**
 * Interface for user management API operations.
 *
 * All methods are scoped to an organization via a mandatory `orgId` path
 * segment. The Real implementation makes actual HTTP calls; the Mock
 * implementation maintains in-memory state for testing.
 */
export interface UsersApiClient {
  /**
   * List members of an organization with pagination.
   *
   * @param orgId - Target org id (active-org id for normal admins, picked org
   *   for staff). Materialized into the URL as `/orgs/{orgId}/users`.
   * @param pagination - Page number (1-indexed) and page size
   * @returns Paginated list of users with metadata
   * @throws HttpError on API errors (e.g. 403 if orgId ≠ active org and the
   *   caller is not staff)
   * @throws SessionExpiredError if authentication fails
   */
  listUsers(
    orgId: string,
    pagination: PaginationRequest,
  ): Promise<ListUsersResponse>;

  /**
   * Invite a new member to an organization.
   *
   * The backend orchestrates: create ZITADEL user → assign org role grant →
   * trigger invite email. The SPA sends only email + role.
   *
   * @param orgId - Target org id (see {@link UsersApiClient.listUsers}).
   * @param email - Email address for the new user
   * @param role - Role to assign (member or admin)
   * @returns The created user (state = USER_STATE_INITIAL)
   * @throws HttpError with status 403 + code SEAT_LIMIT_EXCEEDED if org is at capacity
   * @throws HttpError with status 409 + code DUPLICATE_EMAIL if email already exists in org
   * @throws HttpError on other API errors
   * @throws SessionExpiredError if authentication fails
   */
  inviteUser(
    orgId: string,
    email: string,
    firstName: string,
    lastName: string,
    role: UserRole,
  ): Promise<InviteUserResponse>;

  /**
   * Resend an invitation to an existing member.
   *
   * Generates a new ZITADEL invite code and re-sends the invitation email.
   * Only meaningful for users in USER_STATE_INITIAL.
   *
   * @param orgId - Target org id (see {@link UsersApiClient.listUsers}).
   * @param userId - ID of the user to resend invitation to
   * @returns Success response
   * @throws HttpError with status 404 + code USER_NOT_FOUND if user not found
   * @throws HttpError on other API errors
   * @throws SessionExpiredError if authentication fails
   */
  resendInvite(orgId: string, userId: string): Promise<ResendInviteResponse>;

  /**
   * Change a member's platform role (admin ⇄ member).
   *
   * The backend rewrites the role keys on the user's platform-project grant.
   * Changing your own role is refused (422 CANNOT_MODIFY_SELF), as is changing
   * the role of a user whose access was revoked (409 USER_HAS_NO_ACCESS — a
   * role change must not re-grant access).
   *
   * @param orgId - Target org id (see {@link UsersApiClient.listUsers}).
   * @param userId - ID of the user whose role to change
   * @param role - The role to set (member or admin)
   * @returns Success response, with a `warning` on partial success (the
   *   best-effort org-membership sync failed; the role itself was updated)
   * @throws HttpError with status 404 + code USER_NOT_FOUND if user not found
   * @throws HttpError with status 422 + code CANNOT_MODIFY_SELF on a self-change
   * @throws HttpError with status 409 + code USER_HAS_NO_ACCESS for a revoked user
   * @throws HttpError with status 403 if the caller lacks authority for the org
   * @throws SessionExpiredError if authentication fails
   */
  updateUserRole(
    orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<UpdateUserRoleResponse>;

  /**
   * Remove a member from an organization.
   *
   * The backend removes the user's membership/role grant in the target org.
   * Same-org deletes require `platform.user.admin`; deleting in an org other
   * than the caller's active org requires `platform.staff.admin` (else 403).
   *
   * Resolves on success (the backend returns 204 No Content). The promise's
   * value is intentionally untyped (`void`) — callers act on resolve/reject,
   * not on a body.
   *
   * @param orgId - Target org id (see {@link UsersApiClient.listUsers}).
   * @param userId - ID of the user to remove from the org
   * @throws HttpError with status 404 + code USER_NOT_FOUND if user not found
   * @throws HttpError with status 403 if the caller lacks authority for the org
   * @throws HttpError on other API errors (e.g. a backend guard rail such as
   *   removing the last admin or yourself)
   * @throws SessionExpiredError if authentication fails
   */
  deleteUser(orgId: string, userId: string): Promise<void>;
}

// =============================================================================
// Request Types
// =============================================================================

/**
 * Request body for POST /orgs/{orgId}/users/invite. firstName and lastName are
 * required — ZITADEL mandates a non-empty given and family name on user
 * creation, so the invite form collects them and the backend rejects a blank
 * name with a 400.
 */
interface InviteUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

// =============================================================================
// Constants & Path Builders
// =============================================================================

/**
 * Base path for the tenant-management backend API.
 *
 * The /api/tenant-management/ service prefix is required so the proxy
 * HTTPRoute can route these requests to the tenant-management backend
 * without ambiguity against other /api/* upstreams.
 */
const TM_API_BASE = '/api/tenant-management';

/**
 * Builds the org-scoped members collection path:
 * `/api/tenant-management/orgs/{orgId}/users`.
 *
 * The org id is URL-encoded so ids containing reserved characters can't break
 * out of their path segment.
 */
function orgUsersPath(orgId: string): string {
  return `${TM_API_BASE}/orgs/${encodeURIComponent(orgId)}/users`;
}

// =============================================================================
// Real Implementation
// =============================================================================

/**
 * Real implementation of UsersApiClient that calls the tenant-management
 * backend at /api/tenant-management/orgs/{orgId}/users.
 *
 * The backend response shapes match our User type directly — no transformation
 * needed. The Envoy ext_proc OAuth Proxy injects the Bearer token from the
 * session cookie; requests carry credentials:'include' and X-MP-Token-Handler:1
 * (the latter is handled for /api/* paths by the http module).
 *
 * @example
 * ```typescript
 * const usersClient = new RealUsersApiClient();
 *
 * // List members of an org (page 1, 10 per page)
 * const { items, pagination } = await usersClient.listUsers('org-123', { page: 1, pageSize: 10 });
 *
 * // Invite a new member
 * const { user } = await usersClient.inviteUser('org-123', 'new@example.com', 'member');
 *
 * // Resend invitation
 * await usersClient.resendInvite('org-123', user.id);
 * ```
 */
export class RealUsersApiClient implements UsersApiClient {
  private readonly options?: HttpOptions;

  /**
   * Create a new RealUsersApiClient.
   *
   * @param options - Optional HTTP options (e.g., custom fetch for testing)
   */
  constructor(options?: HttpOptions) {
    this.options = options;
  }

  /**
   * GET /orgs/{orgId}/users?page=N&pageSize=N
   *
   * Page-based pagination (1-indexed). The backend returns our native
   * ListUsersResponse shape — no mapping required.
   */
  async listUsers(
    orgId: string,
    pagination: PaginationRequest,
  ): Promise<ListUsersResponse> {
    const { page, pageSize } = pagination;

    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));

    const url = `${orgUsersPath(orgId)}?${params.toString()}`;
    return httpGet<ListUsersResponse>(url, this.options);
  }

  /**
   * POST /orgs/{orgId}/users/invite
   *
   * Sends {email, firstName, lastName, role}. The backend orchestrates user
   * creation, role grant, and invite email in a single atomic operation.
   * Returns the created user.
   */
  async inviteUser(
    orgId: string,
    email: string,
    firstName: string,
    lastName: string,
    role: UserRole,
  ): Promise<InviteUserResponse> {
    const body: InviteUserRequest = { email, firstName, lastName, role };
    const url = `${orgUsersPath(orgId)}/invite`;
    return httpPost<InviteUserResponse>(url, body, this.options);
  }

  /**
   * POST /orgs/{orgId}/users/{userId}/resend-invite
   *
   * No request body. Generates a new invite code and re-sends the email.
   */
  async resendInvite(
    orgId: string,
    userId: string,
  ): Promise<ResendInviteResponse> {
    const url = `${orgUsersPath(orgId)}/${encodeURIComponent(userId)}/resend-invite`;
    return httpPost<ResendInviteResponse>(url, undefined, this.options);
  }

  /**
   * PUT /orgs/{orgId}/users/{userId}/role
   *
   * Sends {role}. The backend rewrites the user's platform-project role grant
   * and keeps the native org membership in step best-effort (a gap surfaces as
   * `warning` on the response, not an error).
   */
  async updateUserRole(
    orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<UpdateUserRoleResponse> {
    const url = `${orgUsersPath(orgId)}/${encodeURIComponent(userId)}/role`;
    return httpPut<UpdateUserRoleResponse>(url, { role }, this.options);
  }

  /**
   * DELETE /orgs/{orgId}/users/{userId}
   *
   * No request body. The backend returns 204 No Content on success, which the
   * http wrapper resolves to `undefined`.
   */
  async deleteUser(orgId: string, userId: string): Promise<void> {
    const url = `${orgUsersPath(orgId)}/${encodeURIComponent(userId)}`;
    await httpDelete<void>(url, this.options);
  }
}

// =============================================================================
// Default Instance
// =============================================================================

/**
 * Default singleton instance of the Real Users API client.
 *
 * In most cases, import via the registry (./registry.ts) which selects
 * Real vs Mock based on BACKEND_READY and VITE_USE_MOCKS:
 * ```typescript
 * import { usersClient } from './api/registry';
 * ```
 */
export const usersApiClient = new RealUsersApiClient();
